/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import { Row, Col } from 'antd';
import memoize from 'lodash/memoize';
import React from 'react';

import { useSendTestEmail } from '@api/hooks';
import { ArrayList, Button, Modal, Typography, ErrorMessage } from '@atoms';
import { TextField } from '@molecules';

import Form from '../Form';

import { FORM_FIELDS, ADD_FORM_FIELDS, MAX_EMAILS } from './constants';
import s from './styles.module.less';

const EmailTestModal = React.forwardRef(({ id, data = [] }, ref) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const [addForm] = Form.useForm(ADD_FORM_FIELDS);

  const initialValues = React.useMemo(() => form.deserialize(data), [form, data]);
  const [visible, setVisible] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const toggle = React.useCallback((state) => setVisible(typeof state === 'boolean' ? state : !visible), [visible]);

  React.useImperativeHandle(ref, () => ({ toggle }));

  const handleAdd = React.useCallback(
    ({ email }) => {
      const emails = form.getFieldValue(FORM_FIELDS.EMAILS.name) || [];
      form.setFieldsValue({
        [FORM_FIELDS.EMAILS.name]: [email, ...emails],
      });
      addForm.resetFields();
    },
    [form, addForm],
  );

  const [sendTestMutation] = useSendTestEmail({});

  const handleSubmit = React.useCallback(
    async (emails) => {
      setError(null);
      setLoading(true);
      try {
        await sendTestMutation({ id, emails: Object.values(emails) });
        toggle(false);
      } catch (e) {
        setError(e);
      }
      setLoading(false);
    },
    [id, sendTestMutation, setError, toggle],
  );

  const handleRemove = React.useMemo(
    () =>
      memoize((idx) => () => {
        const emails = form.getFieldValue(FORM_FIELDS.EMAILS.name);
        form.setFieldsValue({
          [FORM_FIELDS.EMAILS.name]: [...emails.slice(0, idx), ...emails.slice(idx + 1)],
        });
      }),
    [form],
  );

  const addFormEmailRules = React.useMemo(
    () => [
      ...ADD_FORM_FIELDS.EMAIL.rules,
      ({ getFieldValue }) => ({
        validator: ({ field }) => {
          const emails = form.getFieldValue(FORM_FIELDS.EMAILS.name) || [];
          if (emails.includes(getFieldValue(field))) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('This email address is already in list');
          }
          return Promise.resolve();
        },
      }),
    ],
    [form],
  );

  React.useEffect(() => {
    if (visible) {
      setError(null);
    }
  }, [setError, visible]);

  return (
    <Modal
      visible={visible}
      title="Send a test email"
      subtitle={`Choose up to ${MAX_EMAILS} people to send a test email to`}
      initialValues={initialValues}
      footer={
        <>
          <Button key="submit" type="primary" onClick={form.submit} loading={loading}>
            SEND
          </Button>
          <Button key="back" onClick={toggle}>
            CLOSE
          </Button>
        </>
      }
    >
      <ErrorMessage error={error} variant="block" spaced />

      <Form form={addForm} onFinish={handleAdd} layout="vertical">
        <Row gutter={10} align="bottom">
          <Col flex={1}>
            <TextField {...ADD_FORM_FIELDS.EMAIL} rules={addFormEmailRules} />
          </Col>
          <Col>
            <Button onClick={addForm.submit} className={s.button}>
              ADD
            </Button>
          </Col>
        </Row>
      </Form>

      <Form form={form} onSerializedFinish={handleSubmit} layout="vertical">
        <Form.List {...FORM_FIELDS.EMAILS}>
          {(fields) => {
            const emails = form.getFieldValue(FORM_FIELDS.EMAILS.name) || [];

            return (
              <>
                <Typography.Title level={3} note={fields?.length}>
                  Current recipients
                </Typography.Title>
                <ArrayList>
                  {emails.map((email, idx) => (
                    <ArrayList.Item key={email} onDelete={handleRemove(idx)}>
                      <div className={s.item}>{email}</div>
                    </ArrayList.Item>
                  ))}
                </ArrayList>
              </>
            );
          }}
        </Form.List>
      </Form>
    </Modal>
  );
});

export default EmailTestModal;
